import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, Clock } from 'lucide-react';
import { fetchPostDumpsStatus, processPostDumps, type PostDumpsStatus } from '@/services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface UnprocessedPostsNotificationProps {
    onProcessNow?: () => void;
}

export const UnprocessedPostsNotification = ({ onProcessNow }: UnprocessedPostsNotificationProps) => {
    const [status, setStatus] = useState<PostDumpsStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const loadStatus = async () => {
            try {
                const data = await fetchPostDumpsStatus();
                setStatus(data);
            } catch (error) {
                console.error('Failed to fetch post dumps status:', error);
            } finally {
                setLoading(false);
            }
        };

        loadStatus();
    }, []);

    const handleProcessNow = async () => {
        if (!status?.can_process) return;

        setProcessing(true);
        try {
            await processPostDumps();
            toast.success('Processing started! Your posts will be available shortly.');
            // Refresh status after processing
            const updatedStatus = await fetchPostDumpsStatus();
            setStatus(updatedStatus);
            // Call the callback if provided
            if (onProcessNow) {
                onProcessNow();
            }
        } catch (error) {
            console.error('Failed to process posts:', error);
            toast.error('Failed to start processing. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleUpgrade = () => {
        navigate('/profile?active=subscription');
    };

    // Don't show anything if loading or no status
    if (loading || !status) {
        return null;
    }

    const currentCount = status.current_posts_count ?? 0;
    const maxPostsLimit = status.max_posts_limit ?? 0;
    const isSubscriptionExpired = status.max_posts_limit < currentCount;

    // Case 1: Subscription expired – user is seeing only x posts due to limit
    if (isSubscriptionExpired) {
        return (
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-full">
                            <AlertCircle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-amber-900">
                                You are seeing only {maxPostsLimit} out of your total {currentCount} posts since your subscription is{' '}
                                <span className="text-red-600 font-semibold">expired</span>. Please renew your plan to view all the posts.
                            </h3>
                        </div>
                    </div>
                    <Button
                        onClick={handleUpgrade}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
                    >
                        <AlertCircle className="h-4 w-4" />
                        Renew now
                    </Button>
                </div>
            </div>
        );
    }

    // Case 2: Unprocessed posts – show only when there are pending posts
    if (status.unprocessed_post_dumps_count === 0) {
        return null;
    }

    return (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                        <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-blue-900">
                            You have {status.unprocessed_post_dumps_count} more posts ready to be processed
                        </h3>
                        <p className="text-sm text-blue-700">
                            {status.can_process
                                ? "Ready to process your imported posts and make them searchable"
                                : "Upgrade to Pro to process and organize your posts"
                            }
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {status.can_process ? (
                        <Button
                            onClick={handleProcessNow}
                            disabled={processing}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
                        >
                            {processing ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    PROCESS NOW
                                </>
                            )}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleUpgrade}
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50 px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
                        >
                            <AlertCircle className="h-4 w-4" />
                            Upgrade
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}; 